import router from '@/router';
import RouteNameEnum from '@/router/routeNameEnum';
import {
    getAuthenticationToken,
    postForgotPasword,
    postResetPassword,
} from '@/domains/authentication/api/authentication.api';
import useAuthStore from '@/domains/authentication/store/auth.store';
import useSettingsStore from '@/domains/settings/store/settings.store';
import eventBus from '@/domains/common/services/eventBus';
import NotificationStatus from '@/domains/common/typescript/NotificationStatus';
import HttpStatusCodeEnum from '@/domains/common/typescript/HttpStatusCodeEnum';
import HydraAPI from '@/domains/common/services/hydraApi';
import { handleHttpError } from '@/domains/common/services/errorHandler';
import useResetStore from '@/domains/common/services/useResetStore';
import HttpError from '@/domains/common/typescript/HttpError';
import RoleEnum from '@/domains/authentication/typescript/RoleEnum';
import { growthBookKey } from '@/growthbookKey';
import { t } from '@/translations';
import * as Sentry from '@sentry/vue';
import { inject } from 'vue';
import type LoginCredentialsInterface from '@/domains/authentication/typescript/LoginCredentialsInterface';
import type UserInterface from '@/domains/authentication/typescript/UserInterface';

type JsonErrorBody = {
    code?: number;
};

const login = async ({ username, password }: LoginCredentialsInterface) => {
    try {
        await getAuthenticationToken(username, password);
        await updateToken();
        const authStore = useAuthStore();

        if (authStore.isGranted(RoleEnum.ROLE_ADMIN_ERGO)) {
            void router.push({ name: RouteNameEnum.AdminOffice });
        } else {
            void router.push({ name: RouteNameEnum.SelectOffices });
        }

        eventBus.emit('notification-message', {
            title: t('login.success'),
            status: NotificationStatus.success,
        });

        Sentry.setUser({ email: username });
    } catch (error) {
        const title = error instanceof HttpError ? error.message : t('global.error');
        eventBus.emit('notification-message', {
            title,
            status: NotificationStatus.error,
        });
    }
};

const checkTokenIsExpired = (jsonBody: JsonErrorBody, httpStatus: HttpStatusCodeEnum): void => {
    const authStore = useAuthStore();
    if (
        (httpStatus === HttpStatusCodeEnum.UNAUTHORIZED ||
            (jsonBody.code as HttpStatusCodeEnum) === HttpStatusCodeEnum.UNAUTHORIZED) &&
        authStore.isUserLoggedIn
    ) {
        logout({ shouldLoginRedirect: true });
    }
};

const logout = async ({ shouldLoginRedirect = true }: { shouldLoginRedirect: boolean }) => {
    const { resetAllStores } = useResetStore();
    const authStore = useAuthStore();
    authStore.isUserLoggedIn = false;

    try {
        resetAllStores();
        Sentry.setUser(null);
        // This call site owns its error message: the generic handler would stack a second toast.
        await HydraAPI.getJson('/logout', undefined, { triggerNotification: false });
    } catch (error) {
        // No call site catches this, so a silent failure would leave the practitioner in front of
        // the login page while his session cookie is still valid server-side: a refresh would walk
        // straight back into his account, on the workstation he just left.
        console.error('Logout failed, the server session may still be open', error);
        eventBus.emit('notification-message', {
            title: t('logout.error'),
            status: NotificationStatus.error,
        });
    } finally {
        // The redirect is what unmounts the views, and unmounting is what disposes the 3D scene
        // still showing the previous patient. A failing /logout must not leave it on screen.
        if (shouldLoginRedirect) {
            void router.push({ name: RouteNameEnum.Login });
        }
    }
};

const updateToken = async (to?: { meta: { requiresAuth?: boolean } }): Promise<void> => {
    const authStore = useAuthStore();
    const growthbook = inject(growthBookKey);
    try {
        const settingsStore = useSettingsStore();
        const response = await HydraAPI.getJson<UserInterface>('/users/current-user');
        await authStore.initUserInfos(response);
        if (growthbook) {
            growthbook.setAttributes({
                email: response.email,
            });
        }
        authStore.isUserLoggedIn = true;
        if (authStore.isGranted(RoleEnum.ROLE_ADMIN_ERGO)) {
            return;
        }
        settingsStore.manufacturePreferencesIri = response.manufacturePreferences;
        settingsStore.podonexConfigurationIri = response.podonextConfiguration;
        await settingsStore.fetchManufacturePreferences();
        await settingsStore.fetchPodonexConfiguration();
    } catch {
        if (!to || to.meta.requiresAuth) {
            void router.push({ name: RouteNameEnum.Login });
        }
    } finally {
        authStore.isLoadingUser = false;
    }
};

const forgottenPassword = async (email: string) => {
    const response = await postForgotPasword(email);

    if (response.status === HttpStatusCodeEnum.BAD_REQUEST.valueOf()) {
        handleHttpError(response.status, t('login.forgotten_password_error'));
    }

    void router.push({ name: RouteNameEnum.Login });

    eventBus.emit('notification-message', {
        title: t('login.forgotten_password_success'),
        status: NotificationStatus.success,
    });
};

const resetPassword = async (password: string, token: string) => {
    const response = await postResetPassword(password, token);

    if (response.ok) {
        eventBus.emit('notification-message', {
            title: t('login.reset_password_success'),
            status: NotificationStatus.success,
        });
    } else {
        const error = await response.json();

        handleHttpError(response.status, error.code_error);
    }

    void router.push({ name: RouteNameEnum.Login });
};

export { login, logout, checkTokenIsExpired, updateToken, forgottenPassword, resetPassword };
